import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Btn, Badge, Modal, Input, Loading, EmptyState } from '../components/ui';

export default function DowntimeReasonMaster() {
  const [factories, setFactories] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFactory, setSelectedFactory] = useState(null); // null = landing view
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '' });
  const [error, setError] = useState('');

  useEffect(() => { document.getElementById('lc-page-title').textContent = 'Downtime reason master'; }, []);

  const fetchFactories = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/factories');
      setFactories(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const fetchReasons = useCallback(async (factoryId) => {
    setLoading(true);
    try {
      const { data } = await api.get('/downtime/reasons', { params: { factory_id: factoryId } });
      // only show reasons belonging to this factory (not global ones)
      setReasons(data.filter(r => r.factory_id === factoryId));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchFactories();
  }, [fetchFactories]);

  useEffect(() => {
    if (selectedFactory) fetchReasons(selectedFactory.id);
  }, [selectedFactory, fetchReasons]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '' });
    setError(''); setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name });
    setError(''); setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Reason name is required'); return; }
    try {
      if (editing) {
        await api.put(`/downtime/reasons/${editing.id}`, { name: form.name, is_active: editing.is_active });
      } else {
        await api.post('/downtime/reasons', { name: form.name, factory_id: selectedFactory.id });
      }
      setModalOpen(false); fetchReasons(selectedFactory.id);
    } catch (e) { setError(e.response?.data?.error || 'Error saving'); }
  };

  const handleToggle = async (r) => {
    try {
      await api.put(`/downtime/reasons/${r.id}`, { name: r.name, is_active: !r.is_active });
      fetchReasons(selectedFactory.id);
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete reason "${r.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/downtime/reasons/${r.id}`);
      fetchReasons(selectedFactory.id);
    } catch (e) { alert(e.response?.data?.error || 'Error deleting'); }
  };

  // ── Landing: unit tiles ───────────────────────────────────────────────────
  if (!selectedFactory) {
    return (
      <div style={{ padding: 18 }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>Downtime reason master <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6 }}>{factories.length} units</span></div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Select a unit to manage its downtime reasons</div>
        </div>

        {loading ? <Loading /> : factories.length === 0 ? (
          <EmptyState message="No units found — add factories first" />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12 }}>
            {factories.map(f => (
              <div key={f.id}
                onClick={() => setSelectedFactory(f)}
                style={{ background: 'var(--bg-primary)', border: '0.5px solid var(--border-md)', borderRadius: 12, padding: '18px 20px', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{f.name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginBottom: 10 }}>{f.location}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Badge type={f.status === 'Active' ? 'green' : 'gray'}>{f.status}</Badge>
                  <span style={{ fontSize: 11, color: 'var(--acc)', fontWeight: 500 }}>Open →</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Detail: reasons for selected unit ────────────────────────────────────
  return (
    <div style={{ padding: 18 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <a onClick={() => setSelectedFactory(null)}
          style={{ fontSize: 12, color: 'var(--acc)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
          ← All units
        </a>
        <span style={{ color: 'var(--border-md)' }}>/</span>
        <div>
          <span style={{ fontSize: 15, fontWeight: 500 }}>{selectedFactory.name}</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 8 }}>Downtime reasons</span>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Btn variant="primary" size="sm" onClick={openAdd}>+ Add reason</Btn>
        </div>
      </div>

      {/* Reasons table */}
      <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
        {loading ? <Loading /> : reasons.length === 0
          ? <EmptyState message="No reasons added for this unit yet" action={<Btn variant="primary" onClick={openAdd}>+ Add reason</Btn>} />
          : (
            <table className="lc-table">
              <thead>
                <tr><th>#</th><th>Reason name</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {reasons.map((r, i) => (
                  <tr key={r.id}>
                    <td style={{ color: 'var(--text-tertiary)', fontSize: 11.5 }}>{i + 1}</td>
                    <td style={{ fontWeight: 500 }}>{r.name}</td>
                    <td><Badge type={r.is_active ? 'green' : 'red'}>{r.is_active ? 'Active' : 'Inactive'}</Badge></td>
                    <td style={{ display: 'flex', gap: 10 }}>
                      <a style={{ fontSize: 11, color: 'var(--acc)', cursor: 'pointer' }} onClick={() => openEdit(r)}>Edit</a>
                      <a style={{ fontSize: 11, color: r.is_active ? 'var(--red)' : 'var(--grn)', cursor: 'pointer' }} onClick={() => handleToggle(r)}>
                        {r.is_active ? 'Deactivate' : 'Activate'}
                      </a>
                      <a style={{ fontSize: 11, color: 'var(--red)', cursor: 'pointer' }} onClick={() => handleDelete(r)}>Delete</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {/* Add / Edit modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}
        title={editing ? 'Edit reason' : `Add reason — ${selectedFactory.name}`} width={360}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Reason name</div>
            <Input value={form.name} onChange={v => setForm(p => ({ ...p, name: v }))}
              placeholder="e.g. Machine breakdown" style={{ width: '100%' }} />
          </div>
          {error && <div style={{ fontSize: 11.5, color: 'var(--red)', background: 'var(--red-bg)', padding: '6px 9px', borderRadius: 6 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <Btn onClick={() => setModalOpen(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleSave}>Save</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}